import './SpendConcentration.scss';
import React from "react";
import {useStores} from "~/stores";
import {Button, Grid, TablePagination, Typography} from "@mui/material";
import {SpendConcentrationHelp} from "./SpendConcentrationHelp";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "~/constants.ts";
import {TopOtherVisualization} from "./viz/TopOtherVisualization";
import {observer} from "mobx-react-lite";
import {SpendConcentrationBreadcrumbs} from "./SpendConcentrationBreadcrumbs";
import {SpendConcentrationSearchComponent} from './SpendConcentrationSearchComponent';
import {
    SupplierConsolidationOpportunityModal
} from "~/pages/opportunities-trello/SupplierConsolidationOpportunityModal.tsx";
import {Add, Check} from "@mui/icons-material";
import {runInAction} from "mobx";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {HelpComponent} from "~/components/help-component/HelpComponent.tsx";
import {CARD_CONFIG} from "~/components/dashboard/DashboardCardConfigs.tsx";
import {MithraEchart} from "~/services/echarts/MithraEchart.tsx";
import type {EChartContextType} from "~/services/echarts/EChartProvider.tsx";
import type {EChartsType} from "echarts/types/dist/shared";
import {SCTransactionDetailTableComponent} from "./components/SCTransactionDetailTableComponent.tsx";
import type {MReviewSpencConcentrationSupplier} from "~/services/classes/MaterializedClasses.ts";
import {ParetoOverviewVisualization} from "~/jobs/koi/spend-concentration/viz/ParetoOverviewVisualization.tsx";
import type {Options} from "~/components/visualization/bar-chart/BarChart2.tsx";
import {toCurrency} from "~/components/currency-component/CurrencyClasses.ts";
import {ChartLoadingOverlay} from "~/components/visualization/ChartLoadingOverlay.tsx";

// The category chart plots raw spend amounts (not a 0-1 percentage of total), so it must not use the
// percentage-oriented defaults (valueAxisPercentage/redLine) that ParetoOverviewVisualization otherwise applies.
const categoryChartOptions: Partial<Options> = {
    vertical: false,
    width: 900,
    height: 'auto',
    labelMargin: 300, // Of the total width
    valueLabelPxH: 26,
    valueAxisPercentage: false,
    hideAxis: true,
    padding: 'auto',
    barPadding: 0,
};

function initBarChart(echart: EChartContextType, root: HTMLElement, data: Array<MReviewSpencConcentrationSupplier>, currencySymbol: string): EChartsType {
    const chart = echart.init(root, 'mithra', {
        renderer: 'svg',
        height: 500,
    });
    const series = {
        name: 'Supplier spend',
        type: 'bar',
        barWidth: '60%',
        // p_spend_agg is serialized as a string by DRF's DecimalField (COERCE_DECIMAL_TO_STRING)
        data: data.map(i => Number(i.p_spend_agg)),
    };
    const chartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
            },
            formatter: (params: any) => {
                const point = Array.isArray(params) ? params[0] : params;
                return `${point.name}<br/>${toCurrency(currencySymbol, point.value)}`;
            },
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {show: true},
            },
        },
        grid: {
            bottom: '3%',
            left: '1%',
            right: '1%',
            containLabel: true,
        },
        series: [series],
        xAxis: {
            type: 'category',
            // Passed explicitly (rather than left for echarts to infer from series data) so that suppliers
            // sharing the same name still each get their own tick/bar instead of being collapsed into one.
            data: data.map(i => i.group),
            axisLabel: {
                rotate: 60,
                width: 150,
                overflow: 'truncate',
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value: number) => toCurrency(currencySymbol, value),
            },
        },
    };
    chart.setOption(chartOption);
    return chart;
}

export const SpendConcentration: React.FC = observer(() => {
    const {
        spendConcentrationStore, opportunityStore,
        cmpStore, p
    } = useStores();
    const pageController = spendConcentrationStore.sCPageController;
    const filterController = pageController.filterController;
    let title = 'Spend per supplier';
    if (filterController.selectedCategory.length > 0)
        // Or a breadcrumb here as well???
        title += ' (' + (filterController.selectedCategory.map(l => l === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : l).join(' > ')) + ')';

    const [isOpportunityModelOpen, setOpportunityModalOpen] = React.useState(false);

    const opportunityCreated = opportunityStore.hasOpportunity(cmpStore.cmpCat.selectedOpportunityId)

    return (
        <MainPageFacade
            title={CARD_CONFIG['koi-spend-concentration'].title}
            actionBarStart={<HelpComponent helpContent={<SpendConcentrationHelp/>}/>}
            className="koi-spend-concentration-page">
            <section className="page-item">
                <SupplierConsolidationOpportunityModal
                    isOpen={isOpportunityModelOpen}
                    setOpen={setOpportunityModalOpen}/>

                <Grid container justifyContent="space-evenly">
                </Grid>

                <Grid container justifyContent="center">
                    <Grid size={8}>
                        <Grid container justifyContent="end">
                            <Grid size={8}>
                                <SpendConcentrationBreadcrumbs/>
                            </Grid>
                        </Grid>
                        <ChartLoadingOverlay loading={spendConcentrationStore.sCPageController.categoryChartLoading}>
                            {filterController.canSelectLevelDeeper && <ParetoOverviewVisualization
                                data={spendConcentrationStore.sCPageController.categoryChartData}
                                options={categoryChartOptions}
                                setSelected={d => {
                                    if (filterController.canSelectLevelDeeper) {
                                        filterController.selectNextCategoryDown(d.category);
                                    } else {
                                        filterController.selectCategorySibling(d.category);
                                    }
                                }}
                            />}
                        </ChartLoadingOverlay>
                    </Grid>
                    <Grid size={4}>
                        <ChartLoadingOverlay loading={spendConcentrationStore.sCPageController.topOtherLoading}>
                            {spendConcentrationStore.sCPageController.top20SupplierSpend !== undefined &&
                                spendConcentrationStore.sCPageController.otherThanTop20SupplierSpend !== undefined &&
                                <TopOtherVisualization
                                    topValue={spendConcentrationStore.sCPageController.top20SupplierSpend}
                                    otherValue={spendConcentrationStore.sCPageController.otherThanTop20SupplierSpend}
                                />
                            }
                        </ChartLoadingOverlay>
                    </Grid>
                </Grid>

                <Grid container justifyContent="space-evenly" className="page-item">
                    <Grid size={9}>
                        <Typography variant="h6" className="pareto-chart-title">
                            {title}
                        </Typography>
                    </Grid>
                    <Grid size={3}>
                        <SpendConcentrationSearchComponent/>
                    </Grid>
                </Grid>

                <Grid container justifyContent="space-evenly" className="mb-4">
                    <Grid size={12}>
                        <MithraEchart
                            initChart={(echart, root) => initBarChart(echart, root, spendConcentrationStore.sCPageController.supplierChartData, p.currencySymbol)}
                            data={spendConcentrationStore.sCPageController.supplierChartData}
                            loading={spendConcentrationStore.sCPageController.supplierChartLoading}
                            alwaysRerender={() => {
                            }}
                            onClick={(params) => {
                                filterController.setSupplierSearch(params.name, true);
                                filterController.setSelectedSupplierSpend(params.value as number);
                            }}
                        />
                        <TablePagination
                            count={spendConcentrationStore.sCPageController.supplierChartCount}
                            page={spendConcentrationStore.sCPageController.supplierPage}
                            onPageChange={(_e, p) => spendConcentrationStore.sCPageController.setSupplierPage(p)}
                            rowsPerPage={spendConcentrationStore.sCPageController.supplierPerPage}
                            onRowsPerPageChange={(e) => spendConcentrationStore.sCPageController.setSupplierPerPage(Number(e.target.value))}
                            showFirstButton
                            showLastButton
                            className="float-end"
                        />
                    </Grid>
                </Grid>

                {p.p.enableOpportunityTracker &&
                    <Grid container justifyContent="space-evenly">
                        <Grid>
                            <Button
                                variant="outlined"
                                startIcon={opportunityCreated ? <Check/> : <Add/>}
                                // disabled={!canCreateOpportunity}
                                onClick={() => runInAction(() => {
                                    opportunityStore.isEdit = false;

                                    opportunityStore.editingScOpportunity.title = '';
                                    opportunityStore.editingScOpportunity.id = `${new Date()}`;
                                    opportunityStore.editingScOpportunity.checked = false;
                                    const data = opportunityStore.editingScOpportunity.data;
                                    if (data.type === 'supplier_consolidation') {
                                        data.otherSupplierName = '';
                                        data.opportunitySize = 1000;
                                        data.notes = '';
                                    } else {
                                        console.warn('Experimental...')
                                    }
                                    setOpportunityModalOpen(true);
                                })}>
                                Add opportunity
                            </Button>
                        </Grid>
                    </Grid>
                }
            </section>

            <section className="table-section">
                <SCTransactionDetailTableComponent/>
            </section>
        </MainPageFacade>
    );
});
